import { Field, Form, Formik } from 'formik'
import React, { useMemo } from 'react'
import { PatientHistoryType } from 'src/graphql.generated'
import { Box, Typography } from '@mui/material'
import { FormDatePicker } from 'src/form'
import useAddPatientHistoryEntry from 'src/participantHistory/useAddPatientHistoryEntry'
import IntakeNotePsychiatryFields from 'src/participantHistory/IntakeNotePsychiatryFields'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { DateTime } from 'luxon'
import { useToasts } from 'react-toast-notifications'
import { Loader } from 'src/designSystem'
import { useLoadedNoteDraft } from 'src/noteDraft/useNoteDraft'
import { NoteDraftLoadError } from 'src/noteDraft/NoteDraftAlerts'
import ConfirmNoteDialog from './ConfirmNoteDialog'
import { clearFormFromLocalStorage } from './persistFormHelpers'
import FormPersistor from './FormPersistor'
import { INTAKE_NOTE_PSYCHIATRY_FORM_SCHEMA, IntakeNotePsychiatryFormType } from './IntakeNotePsychiatryFormSchema'

const initialValues = (): IntakeNotePsychiatryFormType => ({
  date: DateTime.now().toFormat('yyyy-MM-dd'),
  visitTypeRemote: false,
  chiefConcern: '',
  currentMediciationsSupplementsAndVitamins: '',
  pastMedications: '',
  sideEffects: '',
  allergies: '',
  physicalAppearance: '',
  hpi: '',
  psychiatricHistory: {
    traumaHistory: '',
    postTraumaticStressSymptoms: '',
    anxietySymptoms: '',
    moodSymptoms: '',
    depressionAndManicSymptoms: '',
    lethalityRisk: '',
    psychoticSymptoms: '',
    sleepPatterns: '',
    appetiteAndNutrition: '',
    sensoryPerceptions: '',
    attentionDeficitSymptoms: '',
  },
  socialAndFunctionalHistory: {
    educationEmploymentStatus: '',
    substanceUseHistory: '',
    medicalHistory: '',
    familyPsychiatricHistory: '',
  },
  therapeuticInterventions: '',
  lifestyleRecommendations: '',
  followUpPlan: '',
  mentalStatusExamination: '',
  clinicalImpression: '',
  BILLING_CODES: [],
})

const FORM_TYPE = PatientHistoryType.IntakeNotePsychiatry

const IntakeNotePsychiatryForm = ({
  uid,
  afterSubmittedCallback,
}: {
  uid: string
  afterSubmittedCallback?: () => void
}) => {
  const addPatientHistoryEntry = useAddPatientHistoryEntry()

  const { addToast } = useToasts()

  const loaded = useLoadedNoteDraft({ uid, type: FORM_TYPE })
  const localStorageFormValues = useMemo(
    () => (loaded.draft as IntakeNotePsychiatryFormType | null) ?? initialValues(),
    [loaded.draft]
  )

  if (loaded.failed) {
    return <NoteDraftLoadError />
  }

  if (!loaded.ready) {
    return <Loader size="large" />
  }

  return (
    <Formik<IntakeNotePsychiatryFormType>
      validationSchema={toFormikValidationSchema(INTAKE_NOTE_PSYCHIATRY_FORM_SCHEMA)}
      initialValues={localStorageFormValues}
      enableReinitialize
      onSubmit={async (values, { resetForm }) => {
        try {
          await addPatientHistoryEntry({
            uid,
            date: values.date,
            type: PatientHistoryType.IntakeNotePsychiatry,
            value: values,
          })
          resetForm({
            values: initialValues(),
          })
          clearFormFromLocalStorage(uid, PatientHistoryType.IntakeNotePsychiatry)
          afterSubmittedCallback?.()
        } catch (e) {
          addToast('Failed to submit Intake Notes (Psychiatry) and create a patient history entry', {
            appearance: 'error',
          })
        }
      }}
    >
      {({ isSubmitting, errors, submitForm }) => (
        <Form>
          <FormPersistor uid={uid} formType={FORM_TYPE} />
          <Box>
            <Field component={FormDatePicker} label="Date of event" name="date" />
          </Box>

          <IntakeNotePsychiatryFields />

          <Box>
            <ConfirmNoteDialog isSubmitting={isSubmitting} onSubmit={submitForm} />
          </Box>

          {Object.values(errors).filter((error) => error).length > 0 && (
            <Box mt={3}>
              <Typography variant="body1" color="error">
                Fill in required fields.
              </Typography>
            </Box>
          )}
        </Form>
      )}
    </Formik>
  )
}

export default IntakeNotePsychiatryForm
